import { ChevronDown, ChevronUp } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/tooltip'

interface MenuOptionsProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  icon: ReactNode
  sidebarOpen?: boolean
  path?: string
  open?: boolean
  className?: string
  isDisabled?: boolean
  isDesktop?: boolean
  isSheetOpen?: boolean
  handleSheetClose?: () => void
}

interface WrapperProps {
  children: ReactNode
  path?: string
  className?: string
  isDisabled?: boolean
  onClick?: (e: React.MouseEvent) => void
}

const Wrapper = ({ children, path, className, onClick }: WrapperProps) => {
  return path ? (
    <NavLink to={path} className={className} onClick={onClick}>
      {children}
    </NavLink>
  ) : (
    <div className={className}>{children}</div>
  )
}

export function MenuOption({
  label,
  path,
  icon,
  open,
  sidebarOpen,
  className,
  isDisabled,
  isSheetOpen,
  handleSheetClose,
}: MenuOptionsProps) {
  const location = useLocation()

  const linkPath = isDisabled ? location.pathname : path

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault()
    } else if (isSheetOpen && handleSheetClose) {
      handleSheetClose()
    }
  }

  return (
    <Wrapper
      path={linkPath}
      onClick={handleClick}
      className={twMerge(
        'text-bios-secondary hover:text-monochromatic-500 aria-[current=page]:text-monochromatic-500 [&>svg]:text-monochromatic-300 [&>svg]:aria-[current=page]:text-bios-primary flex items-center gap-4 overflow-hidden px-6 py-4 text-base font-normal uppercase transition-all duration-300 hover:underline aria-[current=page]:bg-white aria-[current=page]:underline aria-[current=page]:underline-offset-2 2xl:text-lg [&>svg]:size-7',
        !sidebarOpen && 'gap-0.5',
        isDisabled &&
          'text-monochromatic-300! [&>svg]:text-monochromatic-300! cursor-not-allowed no-underline!',
        className,
      )}
    >
      {sidebarOpen ? (
        icon
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{icon}</TooltipTrigger>
            <TooltipContent
              align="start"
              className="text-bios-secondary font-medium"
            >
              {label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {sidebarOpen && <span>{label}</span>}
      {isSheetOpen && <span>{label}</span>}

      {open !== undefined &&
        (open ? (
          <ChevronUp className="text-bios-secondary! ml-auto h-5! w-5! shrink-0" />
        ) : (
          <ChevronDown className="text-bios-secondary! ml-auto h-5! w-5! shrink-0" />
        ))}
    </Wrapper>
  )
}
